import { writable } from 'svelte/store'
import { database } from './indexeddb'
import { createServiceWorkerKeyValueStore } from '../functions/service-worker-key-value-store.js'
import { sendMessageToAppWrapper, isInAppWrapper } from '../functions/app-wrapper-communication.js'
import { APP_WRAPPER_MESSAGE_TYPES } from '../constants.js'

const STORE_NAME = 'received_parcels'

export function isParcelExpired (parcel) {
  if (!parcel) {
    return false
  }

  if (parcel.isExpired) {
    return true
  }

  if (parcel.openedAt != null) {
    const fallbackDuration = 9
    const duration = parcel.duration ?? fallbackDuration
    return (Date.now() - parcel.openedAt) / 1000 > duration
  }

  return false
}

export function createUnopenedParcelsStore () {
  const { subscribe, set } = writable([])

  async function markExpired (parcels) {
    if (!parcels || parcels.length === 0) return
    const tx = database.transaction(STORE_NAME, 'readwrite')
    for (const parcel of parcels) {
      const stored = await tx.store.get(parcel.id)
      if (stored) {
        stored.isExpired = true
        await tx.store.put(stored)
      }
    }
    await tx.done
  }

  async function updateStore (tx) {
    let parcels

    if (tx) {
      parcels = await tx.store.getAll()
    } else {
      parcels = await database.getAll(STORE_NAME)
    }

    const filteredParcels = []
    const expiredParcelsToMark = []

    for (const parcel of parcels) {
      if (isParcelExpired(parcel)) {
        if (!parcel.isExpired) {
          expiredParcelsToMark.push(parcel)
        }
      } else {
        filteredParcels.push(parcel)
      }
    }

    set(filteredParcels)

    const count = parseInt(filteredParcels.length) || 0

    if ('setAppBadge' in window.navigator) {
      window.navigator.setAppBadge(count)

      createServiceWorkerKeyValueStore()
        .then((swKeyValueStore) => {
          // It's own then-callback, because i'm not sure if there are any problems
          // opening two database at the same time...
          swKeyValueStore.set('badgeCount', count)
        })
    }

    if (isInAppWrapper()) {
      sendMessageToAppWrapper({ type: APP_WRAPPER_MESSAGE_TYPES.OUTGOING.NOTIFICATION_SET_BADGE, value: count })
    }

    if (expiredParcelsToMark.length > 0 && !tx) {
      markExpired(expiredParcelsToMark).catch(console.error)
    }

    return parcels
  }

  function load () {
    return updateStore()
  }

  return {
    subscribe,
    load,

    async put (parcel) {
      await database.put(STORE_NAME, parcel)
      await load()
    },

    get (id) {
      return database.get(STORE_NAME, id)
    },

    async getAllExpired () {
      const tx = database.transaction(STORE_NAME, 'readonly')
      const result = []

      let cursor = await tx.store.openCursor()

      while (cursor) {
        if (isParcelExpired(cursor.value)) {
          result.push(cursor.value)
        }

        cursor = await cursor.continue()
      }

      return result
    },

    async deleteByIds (ids) {
      if (!ids || ids.length === 0) return
      const tx = database.transaction(STORE_NAME, 'readwrite')

      for (const id of ids) {
        await tx.store.delete(id)
      }

      await tx.done
      await load()
    },

    async setExpired (id) {
      const tx = database.transaction(STORE_NAME, 'readwrite')
      const parcel = await tx.store.get(id)

      if (parcel) {
        parcel.isExpired = true
        await tx.store.put(parcel)
      }

      await tx.done
      await load()
    },

    async setAll (parcels, contactIds) {
      const tx = database.transaction(STORE_NAME, 'readwrite')

      const currentServerParcelIds = new Set(parcels.map((parcel) => parcel.id))
      const allKnownParcels = await tx.store.getAll()
      const allKnownIds = allKnownParcels.map((parcel) => parcel.id)

      for (const knownParcel of allKnownParcels) {
        if (isParcelExpired(knownParcel)) {
          if (!knownParcel.isExpired) {
            knownParcel.isExpired = true
            await tx.store.put(knownParcel)
          }
          continue
        }

        if (!currentServerParcelIds.has(knownParcel.id)) {
          if (knownParcel.openedAt != null || !knownParcel.isCached) {
            knownParcel.isExpired = true
            await tx.store.put(knownParcel)
          }
        }
      }

      for (const parcel of parcels) {
        if (allKnownIds.includes(parcel.id)) {
          // All parcels that are already persisted (including expired) should not
          // get overwritten, because the may include a changed property (like openedAt).
          continue
        }

        if (!contactIds.includes(parcel.uploadedBy)) {
          console.log(`Parcel "${parcel.id}" won't be downloaded because no corresponding contact to id "${parcel.uploadedBy}" can be found.`)
          continue
        }

        await tx.store.put({
          id: parcel.id,
          type: parcel.type,
          uploadedBy: parcel.uploadedBy,
          uploadedAt: parcel.uploadedAt,
          content: parcel.content,
          isCached: false,
          openedAt: null,
          isExpired: false
        })
      }

      const parcelsInStore = await updateStore(tx)

      await tx.done

      return parcelsInStore
    },

    async clearAll () {
      await database.clear(STORE_NAME)
      await load()
    }
  }
}
